import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import { LogoMark, LogoWord } from '../components/Logo';
import './AccountShell.css';

const HEADER_H = 58;
const RAIL_W = 64;

const menuItem = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: `${space.s2}px ${space.s3}px`, borderRadius: radius.sm,
  fontFamily: font.display, fontSize: 13, fontWeight: 600,
  textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
};

function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 11.2 12 4l9 7.2M5.2 9.6V20h4.2v-5.4h5.2V20h4.2V9.6"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClubsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.2 19 5.6v5.2c0 4.3-3 7.6-7 8.8-4-1.2-7-4.5-7-8.8V5.6L12 3.2Z"
        stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9.4 11.3 11.2 13l3.4-3.4" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NewsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.5 10v4a1.5 1.5 0 0 0 1.5 1.5h2L16 20V4l-9 4.5H5A1.5 1.5 0 0 0 3.5 10Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M7 15.5V18a1.5 1.5 0 0 0 3 0v-1.2" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 9.4c.9.7 1.4 1.7 1.4 2.6s-.5 1.9-1.4 2.6" stroke="currentColor"
        strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SavesIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 6.5C5 5.1 8.1 4 12 4s7 1.1 7 2.5M5 6.5v11c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-11M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M7 5.5H4.6V7a3 3 0 0 0 3 3M17 5.5h2.4V7a3 3 0 0 1-3 3M12 13v3.4M8.5 20h7M9.5 20c0-1.5 1-2.1 2.5-2.1s2.5.6 2.5 2.1"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavIcon({ to, end, label, children }) {
  return (
    <NavLink to={to} end={end} title={label} aria-label={label} className="gw-rail-link"
      style={{
        position: 'relative', width: '100%', height: 48,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span style={{
              position: 'absolute', right: 0, top: 10, bottom: 10, width: 3,
              borderRadius: '3px 0 0 3px', background: accent.lime,
            }} />
          )}
          {children}
        </>
      )}
    </NavLink>
  );
}

// Initials avatar in the header; opens a dropdown with Account settings + Sign out.
function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const meta = user?.user_metadata || {};
  const initials = (((meta.firstName || '')[0] || '') + ((meta.lastName || '')[0] || ''))
    || ((user?.email || '?')[0] || '?');
  const name = [meta.firstName, meta.lastName].filter(Boolean).join(' ');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="gw-acct-circle"
        aria-label="Account menu"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 36, height: 36, borderRadius: '50%', cursor: 'pointer',
          background: accent.cyan, border: 'none', color: bg.base,
          fontFamily: font.display, fontWeight: 700, fontSize: 13, letterSpacing: '0.02em',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'box-shadow .14s',
        }}
      >
        {initials.toUpperCase()}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 10px)', minWidth: 224,
          background: bg.overlay, border: `1px solid ${line.strong}`, borderRadius: radius.md,
          boxShadow: '0 16px 44px rgba(0,0,0,0.55)', padding: space.s2, zIndex: 50,
        }}>
          <div style={{ padding: `${space.s2}px ${space.s3}px ${space.s3}px` }}>
            {name && <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 14, color: tx.primary }}>{name}</div>}
            <div style={{ fontFamily: font.data, fontSize: 11, color: tx.muted, marginTop: 2, wordBreak: 'break-all' }}>
              {user?.email}
            </div>
          </div>
          <div style={{ height: 1, background: line.DEFAULT, margin: `${space.s2}px 0` }} />
          <Link to="/hub/settings" className="gw-acct-item" style={menuItem} onClick={() => setOpen(false)}>
            Account settings
          </Link>
          <button type="button" className="gw-acct-item gw-acct-item--danger" style={menuItem} onClick={signOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// Hub chrome: lime brand block top-left, wordmark in the header, icon rail down the left.
export default function AccountShell() {
  return (
    <div style={{ minHeight: '100vh', background: bg.base }}>
      <header style={{
        position: 'sticky', top: 0, zIndex: 20, display: 'flex',
        height: HEADER_H, background: bg.surface, borderBottom: `1px solid ${line.DEFAULT}`,
      }}>
        <Link
          to="/hub"
          aria-label="Groundwork"
          style={{
            width: RAIL_W, height: HEADER_H, flexShrink: 0,
            background: 'radial-gradient(130% 145% at 50% -25%, #F2FF8E 0%, #D2F646 36%, #A4CA1C 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <LogoMark height={34} colour={bg.base} />
        </Link>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: `0 ${space.s5}px`, minWidth: 0 }}>
          <LogoWord height={15} />
          <div style={{ marginLeft: 'auto' }}>
            <AccountMenu />
          </div>
        </div>
      </header>

      <div style={{ display: 'flex', minHeight: `calc(100vh - ${HEADER_H}px)` }}>
        <aside style={{
          width: RAIL_W, flexShrink: 0, background: bg.surface, zIndex: 10,
          borderRight: `1px solid ${line.DEFAULT}`,
          position: 'sticky', top: HEADER_H, height: `calc(100vh - ${HEADER_H}px)`,
          display: 'flex', flexDirection: 'column', gap: space.s2, padding: `${space.s4}px 0`,
        }}>
          <NavIcon to="/hub" end label="Home"><HomeIcon /></NavIcon>
          <NavIcon to="/hub/clubs" label="Explore clubs"><ClubsIcon /></NavIcon>
          <NavIcon to="/hub/news" label="Groundwork news"><NewsIcon /></NavIcon>
          <NavIcon to="/hub/saves" label="Manage saves"><SavesIcon /></NavIcon>
          <NavIcon to="/hub/achievements" label="Achievements"><TrophyIcon /></NavIcon>
        </aside>

        <main style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
